import React, { useEffect, memo } from 'react';
import { PROGRAMS_NAMES } from '@/data';
import { ProgramContainer, Widget, WidgetHeader } from 'components/fragment';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { selectHighlightedDates } from 'store/app/app.selector';
import { SET_HIGHLIGHTED_DATE } from 'store/app/app.actions';

const Column: React.FC<{
  column: string[];
  columnId: string;
  index: number;
  isFixed?: boolean;
  isLastColumn?: boolean;
  onAddWidget: (columnId: string, index: number) => void;
  onAddColumn: () => void;
  onRemoveColumn: (columnId: string) => void;
  onSplit: (columnId: string) => void;
  onFullColumn: (columnId: string) => void;
  onPin: (columnId: string) => void;
  hasScroll: boolean;
  isMobile?: boolean;
  onProgramFullScreen?: (programId: string) => boolean;
  onProgramOptions?: () => void;
  onProgramRemove?: (programId: string) => void;
  programIdInFullScreen?: string;
  quickPickList: string[];
  onAddProgram: (programId?: string, columnId?: string, index?: number) => void;
  dynamicSize: number;
  notificationType: string;
}> = ({
  column,
  columnId,
  onAddWidget,
  onAddColumn,
  onRemoveColumn,
  onFullColumn,
  onSplit,
  onPin,
  isFixed = false,
  isLastColumn = false,
  hasScroll,
  isMobile,
  onProgramFullScreen,
  onProgramRemove,
  programIdInFullScreen,
  quickPickList,
  onAddProgram,
  dynamicSize,
  notificationType,
}) => {
  const [highlightedDate, setHighlightedDate] = React.useState<string>('');
  const highlightedDates = useAppSelector(selectHighlightedDates);
  const dispatch = useAppDispatch();
  const isSplit = column.length > 1;
  let showAddBtn = false;
  const heightFixed =
    notificationType === 'marquee' ? 'h-[calc(100%-100px)]' : 'h-[calc(100%-75px)]';
  const heightFullScreen =
    notificationType === 'marquee' ? 'h-[calc(100%-111px)]' : 'h-[calc(100%-95px)]';

  useEffect(() => {
    column.forEach((value) => {
      const highlightedDate = highlightedDates.find((d) => d.programId === value)?.date;
      if (!highlightedDate) {
        dispatch(SET_HIGHLIGHTED_DATE({ programId: value, date: new Date().toISOString() }));
      }
      setHighlightedDate(highlightedDate || new Date().toISOString());
    });
  }, [column, highlightedDates, dispatch]);

  return (
    <div
      className={`shrink-0 flex-row p-1 ${
        isFixed && !isMobile
          ? `bg-white-50 fixed left-0 z-10 ${heightFixed} min-w-[317px]`
          : 'h-full min-w-[317px] flex-1'
      } ${isLastColumn ? 'group relative h-full' : ''} ${hasScroll && isFixed ? 'shadow-[10px_0_20px_-5px_rgba(0,0,0,0.25)]' : ''} overflow-hidden`}
      //edge case for only one column width is 100%, for 2 columns width is 50% else set it dynamically
      style={{
        width: isFixed
          ? isLastColumn || isMobile
            ? '100%'
            : dynamicSize
              ? `${dynamicSize}px`
              : '50%'
          : '',
      }}>
      {column.map((value: string, widgetIndex) => {
        const foundProgram = PROGRAMS_NAMES.find((program) => program.value === value);
        if (foundProgram && isLastColumn) showAddBtn = true;
        // isActiveFullScreen: this specific program is the visible full-screen program (for CSS styling)
        const isActiveFullScreen = programIdInFullScreen
          ? programIdInFullScreen.toString() === foundProgram?.value
          : false;
        // isFullScreen: dashboard is in full-screen mode (for child components)
        // Screen width ≤767px forces isFullScreen=false regardless of column view state
        const isFullScreen = window.innerWidth > 767 && !!programIdInFullScreen;
        const hidePinSplit = widgetIndex === 1;

        // PERFORMANCE: Use CSS to hide instead of unmounting to preserve component state
        const shouldHide = programIdInFullScreen && !isActiveFullScreen;

        return (
          <div
            key={foundProgram?.label + widgetIndex}
            className={`w-full ${isSplit && !isActiveFullScreen ? 'h-[50%]' : !isActiveFullScreen ? 'h-full' : ''} ${isActiveFullScreen ? `bg-white-50 fixed top-[120px] left-0 z-1000 p-1 ${heightFullScreen}` : ''}`}
            style={shouldHide ? { display: 'none' } : undefined}>
            {!isActiveFullScreen && (
              <WidgetHeader
                isFixed={isFixed}
                isSplit={isSplit}
                onSplit={() => onSplit(columnId)}
                onFullColumn={() => onFullColumn(columnId)}
                onPin={() => onPin(columnId)}
                programTitle={foundProgram?.label || ''}
                isFullScreen={isActiveFullScreen}
                onProgramFullScreen={onProgramFullScreen}
                programId={value}
                columnId={columnId}
                onProgramRemove={onProgramRemove}
                hidePinSplit={hidePinSplit}
                onRemoveColumn={onRemoveColumn}
              />
            )}
            {foundProgram ? (
              <ProgramContainer
                title={foundProgram.label}
                programId={value}
                isSplit={isSplit}
                onProgramRemove={onProgramRemove}
                onProgramFullScreen={onProgramFullScreen}
                isFullScreen={isFullScreen}
                highlightedDate={highlightedDate}
                isHidden={shouldHide}
              />
            ) : (
              <Widget
                columnId={columnId}
                widgetIndex={widgetIndex}
                isSplit={isSplit}
                isLastColumn={isLastColumn}
                onAddWidget={onAddWidget}
                onAddColumn={onAddColumn}
                quickPickList={quickPickList}
                onAddProgram={onAddProgram}
              />
            )}
          </div>
        );
      })}
    </div>
  );
};

export default memo(Column);
